import { cronError, isValidTimezone, nextCronFire, type ParsedCron, parseCron } from './cron'

/**
 * How often a task starts itself. Lives on the task, not on the task version: changing a cadence is an
 * operational decision, and cutting a new immutable version for it would bury the definition history under
 * "ran hourly, then half-hourly". Runs a schedule starts carry the task's current version like any other run.
 */
export type TaskSchedule = {
    /** Five-field cron, read in `timezone`. */
    cron: string
    /**
     * IANA zone (`Europe/London`). The cadence follows the zone's DST, so 09:00 stays 09:00. Or the
     * {@link FOLLOW_ENTERPRISE_TIMEZONE} sentinel, which fires against whatever the enterprise is set to
     * (#884) — every function here resolves it with {@link resolveScheduleTimezone} first.
     */
    timezone: string
    /** Off keeps the cadence written down without firing it. */
    enabled: boolean
    /** Run input for each firing, the same shape the Start form and the webhook body post. */
    input?: Record<string, unknown> | null
    /**
     * The last firing the OS took without starting a run, because the input (after the task version's defaults)
     * left a required field unset (#1395). Written by the worker, never by a save: saving the schedule again, or the
     * next firing that does start, clears it.
     */
    skipped?: ScheduleSkip | null
}

/** A firing that was consumed and started nothing: when, and the required input field it had no value for. */
export type ScheduleSkip = { at: string; error: 'input_required'; field: string }

export const DEFAULT_SCHEDULE_TIMEZONE = 'UTC'

/**
 * The sentinel a schedule's own `timezone` field holds to mean "whatever this enterprise is set to" rather
 * than naming a zone of its own (#884). Not an IANA name, so it can never collide with one; resolved with
 * {@link resolveScheduleTimezone} wherever a schedule is parsed, fired, or described.
 */
export const FOLLOW_ENTERPRISE_TIMEZONE = 'enterprise'

/** The cadences worth one click. `custom` hands the CEO the cron field itself. */
export const SCHEDULE_PRESETS: Array<{ cron: string; label: string }> = [
    { cron: '*/5 * * * *', label: 'Every 5 minutes' },
    { cron: '*/15 * * * *', label: 'Every 15 minutes' },
    { cron: '*/30 * * * *', label: 'Every 30 minutes' },
    { cron: '0 * * * *', label: 'Hourly, on the hour' },
    { cron: '0 */4 * * *', label: 'Every 4 hours' },
    { cron: '0 9 * * *', label: 'Every day at 09:00' },
    { cron: '0 9 * * 1-5', label: 'Weekdays at 09:00' },
    { cron: '0 9 * * 1', label: 'Mondays at 09:00' },
    { cron: '0 9 1 * *', label: 'The 1st of each month at 09:00' },
]

/**
 * Why this schedule cannot be saved, as a sentence for a form, or null when it is fine. A schedule that
 * follows the enterprise is read in `enterpriseTimezone`, so the zone it is actually checked against is the
 * one it would actually fire in.
 */
export function scheduleError(schedule: TaskSchedule, enterpriseTimezone?: string): string | null {
    const cron = cronError(schedule.cron)
    if (cron) {
        return cron
    }
    const timezone = resolveScheduleTimezone(schedule, enterpriseTimezone)
    if (!isValidTimezone(timezone)) {
        return `${timezone} is not a timezone this system knows.`
    }
    return null
}

export function parseSchedule(schedule: TaskSchedule): ParsedCron {
    return parseCron(schedule.cron)
}

/**
 * When an enabled schedule next fires after `after`; null when it is off or fires nothing within a year.
 * `enterpriseTimezone` is the zone a schedule that follows the enterprise is read in: pass the enterprise's
 * current value, never a remembered one, so changing it moves the next firing (#884).
 */
export function nextScheduleFire(
    schedule: TaskSchedule,
    after: Date = new Date(),
    enterpriseTimezone?: string,
): Date | null {
    if (!schedule.enabled || scheduleError(schedule, enterpriseTimezone)) {
        return null
    }
    return nextCronFire(parseCron(schedule.cron), resolveScheduleTimezone(schedule, enterpriseTimezone), after)
}

const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/**
 * The cadence as a sentence ("Every 15 minutes"). Falls back to the expression itself, so an exotic cron reads
 * as the cron it is rather than as a wrong summary.
 */
export function describeCron(expression: string): string {
    const preset = SCHEDULE_PRESETS.find(row => row.cron === expression.trim())
    if (preset) {
        return preset.label
    }
    if (cronError(expression)) {
        return expression.trim()
    }
    const [minute, hour, dayOfMonth, month, dayOfWeek] = expression.trim().split(/\s+/) as [
        string,
        string,
        string,
        string,
        string,
    ]
    const everyDay = dayOfMonth === '*' && month === '*' && dayOfWeek === '*'
    const stepMinutes = /^\*\/(\d+)$/.exec(minute)
    if (stepMinutes && hour === '*' && everyDay) {
        return `Every ${stepMinutes[1]} minutes`
    }
    const stepHours = /^\*\/(\d+)$/.exec(hour)
    if (/^\d+$/.test(minute) && stepHours && everyDay) {
        return `Every ${stepHours[1]} hours, at ${minute.padStart(2, '0')} past`
    }
    if (/^\d+$/.test(minute) && hour === '*' && everyDay) {
        return `Hourly, at ${minute.padStart(2, '0')} past`
    }
    const at = clockTime(minute, hour)
    if (!at) {
        return expression.trim()
    }
    if (everyDay) {
        return `Every day at ${at}`
    }
    if (dayOfMonth === '*' && month === '*') {
        const days = weekdayLabel(dayOfWeek)
        return days ? `${days} at ${at}` : expression.trim()
    }
    if (/^\d+$/.test(dayOfMonth) && month === '*' && dayOfWeek === '*') {
        return `Day ${dayOfMonth} of each month at ${at}`
    }
    return expression.trim()
}

/**
 * The cadence plus its zone, for a line under the task title. A schedule that follows the enterprise reads as
 * the zone it fires in rather than as the sentinel, so nobody has to know the sentinel exists.
 */
export function describeSchedule(schedule: TaskSchedule, enterpriseTimezone?: string): string {
    const cadence = describeCron(schedule.cron)
    const timezone = resolveScheduleTimezone(schedule, enterpriseTimezone)
    return timezone === 'UTC' ? `${cadence} UTC` : `${cadence} (${timezone})`
}

function clockTime(minute: string, hour: string): string | null {
    if (!/^\d+$/.test(minute) || !/^\d+$/.test(hour)) {
        return null
    }
    return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`
}

function weekdayLabel(dayOfWeek: string): string | null {
    if (dayOfWeek === '1-5') {
        return 'Weekdays'
    }
    if (dayOfWeek === '0,6' || dayOfWeek === '6,0') {
        return 'Weekends'
    }
    const days = dayOfWeek.split(',').map(part => (part === '7' ? 0 : Number(part)))
    if (days.some(day => !Number.isInteger(day) || day < 0 || day > 6)) {
        return null
    }
    return days.map(day => `${DAY_LABELS[day]}s`).join(' and ')
}

/**
 * The zone a schedule actually fires in: the enterprise's own zone when the schedule follows it
 * (`FOLLOW_ENTERPRISE_TIMEZONE`), otherwise the zone the schedule names for itself (#884).
 *
 * `enterpriseTimezone` is optional so a caller with no enterprise to hand — a preview in a form, a unit that
 * only has the schedule — still gets an answer rather than an empty string; it falls back to
 * {@link DEFAULT_SCHEDULE_TIMEZONE}. Anything that actually fires a schedule passes the enterprise's current
 * zone, read fresh, so changing it moves the next firing.
 */
export function resolveScheduleTimezone(schedule: TaskSchedule, enterpriseTimezone?: string): string {
    if (schedule.timezone !== FOLLOW_ENTERPRISE_TIMEZONE) {
        return schedule.timezone
    }
    return enterpriseTimezone?.trim() ? enterpriseTimezone : DEFAULT_SCHEDULE_TIMEZONE
}
